import type { Icon } from '@phosphor-icons/react';

import {
  CheckCircleIcon,
  QuestionIcon,
  WarningCircleIcon,
  WrenchIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';

import Color from '../../constants/Color';

export type UptimeState =
  | 'operational'
  | 'degraded'
  | 'outage'
  | 'maintenance'
  | 'unknown';

export type UptimeDay = {
  day: string;
  status: string;
  downtimeDuration: number;
  maintenanceDuration: number;
};

export const DESKTOP_SEGMENT_COUNT = 90;
export const MOBILE_SEGMENT_COUNT = 30;

/**
 * Better Stack reports `aggregate_state` and resource `status` in lower case
 * (`operational`, `downtime`, `degraded`, `maintenance`), but other providers
 * and older payloads use aliases. Fold them all onto one state.
 */
export function stateFromStatus(raw: string): UptimeState {
  switch (raw.toLowerCase()) {
    case 'up':
    case 'operational':
    case 'online':
      return 'operational';
    case 'degraded':
    case 'partial_outage':
    case 'partial':
    case 'minor':
      return 'degraded';
    case 'down':
    case 'downtime':
    case 'outage':
    case 'major_outage':
    case 'major':
    case 'offline':
      return 'outage';
    case 'maintenance':
    case 'paused':
      return 'maintenance';
    default:
      return 'unknown';
  }
}

type StateVisual = {
  foreground: string;
  background: string;
  label: string;
  icon: Icon;
};

export const STATE_VISUALS: Record<UptimeState, StateVisual> = {
  operational: {
    foreground: Color.greenLight,
    background: Color.greenLightBg,
    label: 'Operational',
    icon: CheckCircleIcon,
  },
  degraded: {
    foreground: Color.yellow,
    background: Color.yellowBg,
    label: 'Degraded',
    icon: WarningCircleIcon,
  },
  outage: {
    foreground: Color.accent,
    background: Color.accentBg,
    label: 'Downtime',
    icon: XCircleIcon,
  },
  maintenance: {
    foreground: Color.blue,
    background: Color.blueBg,
    label: 'Maintenance',
    icon: WrenchIcon,
  },
  unknown: {
    foreground: Color.neutral40,
    background: Color.neutral10,
    label: 'Unknown',
    icon: QuestionIcon,
  },
};

/** The states shown in the bar legend, in reading order. */
export const LEGEND_STATES: UptimeState[] = [
  'operational',
  'degraded',
  'outage',
  'maintenance',
];

export function bannerCopy(
  state: UptimeState,
  resourceCount: number,
  affectedCount: number
): { headline: string; subline: string } {
  switch (state) {
    case 'operational':
      return {
        headline: 'All systems operational',
        subline: `${resourceCount} services monitored`,
      };
    case 'degraded':
      return {
        headline: 'Some systems are degraded',
        subline: `${affectedCount} of ${resourceCount} services affected`,
      };
    case 'outage':
      return {
        headline: 'Systems are experiencing an outage',
        subline: `${affectedCount} of ${resourceCount} services affected`,
      };
    case 'maintenance':
      return {
        headline: 'Scheduled maintenance in progress',
        subline: 'Some services are under maintenance',
      };
    case 'unknown':
      return {
        headline: 'Status unavailable',
        subline: "We couldn't read the current status. Please try again later.",
      };
  }
}

/**
 * The provider reports availability as a 0-1 fraction (confirmed against the
 * backend fixtures), so it is converted to a percentage exactly once.
 */
export function formatAvailability(value: number | null): string {
  if (value === null) return '—';
  return `${(value * 100).toFixed(3)}%`;
}

/**
 * Formats a duration in seconds for the tooltip, from the raw provider value.
 * Durations are whole seconds, so sub-minute values round up to avoid a
 * misleading `0s`.
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
}

export function formatDayLabel(day: string): string {
  return format(parseISO(day), 'd MMM yyyy');
}

/**
 * The worst state reported for a day: any downtime makes the whole day an
 * outage, then any maintenance, otherwise the reported status decides.
 */
export function dayState(day: UptimeDay): UptimeState {
  if (day.downtimeDuration > 0) return 'outage';
  if (day.maintenanceDuration > 0) return 'maintenance';
  return stateFromStatus(day.status);
}

/**
 * The bar cell colour for a day, or null for an unknown day so the cell keeps
 * the neutral track instead of a state colour (design rev 5 §7.2).
 */
export function daySegmentColor(day: UptimeDay): string | null {
  const state = dayState(day);
  return state === 'unknown' ? null : STATE_VISUALS[state].foreground;
}

/** The second tooltip line, describing the day's downtime and maintenance. */
export function tooltipDetail(
  state: UptimeState,
  downtimeDuration: number,
  maintenanceDuration: number
): string {
  const hasDowntime = downtimeDuration > 0;
  const hasMaintenance = maintenanceDuration > 0;

  if (hasDowntime && hasMaintenance) {
    return `${formatDuration(downtimeDuration)} downtime · ${formatDuration(
      maintenanceDuration
    )} maintenance`;
  }
  if (hasDowntime) return `${formatDuration(downtimeDuration)} downtime`;
  if (hasMaintenance) {
    return `${formatDuration(maintenanceDuration)} maintenance`;
  }

  switch (state) {
    case 'operational':
      return 'No downtime';
    case 'degraded':
      return 'Degraded';
    case 'outage':
      return 'Outage';
    case 'maintenance':
      return 'Maintenance';
    default:
      return 'No data';
  }
}

/**
 * Pads or trims the history to exactly `count` slots, oldest on the left, so
 * the newest day always sits flush right.
 */
export function toDaySlots<T extends UptimeDay>(
  statusHistory: ReadonlyArray<T>,
  count: number
): Array<T | null> {
  const recent = statusHistory.slice(-count);
  const padding = count - recent.length;
  return [...Array.from({ length: padding }, () => null), ...recent];
}
